import { Building2, CheckCircle2, Download, Lock, Star, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { MarketplaceAgent } from "@/api/types";
import { Badge } from "@/components/ui/badge";

interface Props {
  agent: MarketplaceAgent;
}

export function MarketplaceCard({ agent }: Props) {
  const { t } = useTranslation("marketplace");
  const navigate = useNavigate();

  const visibilityLabel = (() => {
    switch (agent.visibility) {
      case "company":
        return t("visibility.company");
      case "team_restricted":
        return t("visibility.teamRestricted");
      case "private":
        return t("visibility.private");
      default:
        return agent.visibility;
    }
  })();
  const VisibilityIcon = (() => {
    switch (agent.visibility) {
      case "company":
        return Building2;
      case "team_restricted":
        return Users;
      case "private":
        return Lock;
      default:
        return Users;
    }
  })();

  return (
    <button
      type="button"
      onClick={() => navigate(`/marketplace/${agent.id}`)}
      className="group flex flex-col items-start gap-2 rounded-md border bg-card p-4 text-left transition hover:border-primary/50"
    >
      <div className="flex w-full items-center justify-between gap-2">
        <h3 className="line-clamp-1 text-base font-semibold tracking-tight">
          {agent.name}
        </h3>
        {agent.is_installed ? (
          <Badge variant="outline" className="gap-1 text-xs">
            <CheckCircle2 className="h-3 w-3 text-green-700" />
            {t("badges.installed")}
          </Badge>
        ) : null}
      </div>
      <p className="line-clamp-2 text-sm text-muted-foreground">
        {agent.description}
      </p>
      <div className="mt-auto flex w-full items-center gap-3 pt-2 text-xs text-muted-foreground">
        {agent.rating_avg !== null ? (
          <span className="inline-flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
            {agent.rating_avg.toFixed(1)}
            <span className="text-muted-foreground/70">
              ({agent.rating_count})
            </span>
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <Download className="h-3 w-3" />
          {agent.install_count}
        </span>
        <span
          className="inline-flex items-center gap-1"
          title={visibilityLabel}
        >
          <VisibilityIcon className="h-3 w-3" />
          {visibilityLabel}
        </span>
      </div>
    </button>
  );
}
